import React, { useState, useEffect } from 'react';
import { X, Check, DollarSign, RefreshCw, AlertCircle, Shield, Zap, Star } from 'lucide-react';

const SubscriptionModal = ({ isOpen, onClose, onConfirm, user, preselectedPlan = null, onPaymentSetupRequired = null }) => {
  const [selectedPlan, setSelectedPlan] = useState(preselectedPlan || '');
  const [loading, setLoading] = useState(false);
  const [autoRenew, setAutoRenew] = useState(true);
  const [confirmationStep, setConfirmationStep] = useState(false);
  const [needsPaymentSetup, setNeedsPaymentSetup] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
  
  const SUBSCRIPTION_PRICING = {
    Monthly: { price: 20.00, label: 'Monthly', period: 'month', savings: null },
    Annual: { price: 200.00, label: 'Annual', period: 'year', savings: 'Save $40/year' }
  };

  // Reset states when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      setConfirmationStep(false);
      if (preselectedPlan) {
        setSelectedPlan(preselectedPlan);
      }
    }
  }, [isOpen, preselectedPlan]);

  const handleProceedToConfirmation = () => {
    if (!selectedPlan) {
      alert('Please select a subscription plan');
      return;
    }
    // Payment method already verified before modal opened, proceed to confirmation
    setConfirmationStep(true);
  };

  const handleFinalConfirmation = async () => {
    if (!selectedPlan) return;

    // Check if user has payment method
    if (!user?.payment_method && !user?.stripe_customer_id) {
      setNeedsPaymentSetup(true);
      
      // If callback provided, trigger payment setup modal
      if (onPaymentSetupRequired) {
        onPaymentSetupRequired();
      } else {
        alert('Please add a payment method before subscribing. Go to your Profile to add a payment method.');
      }
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/subscriptions/subscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          subscription_type: selectedPlan,
          auto_renew: autoRenew
        })
      });

      if (response.ok) {
        const data = await response.json();
        onConfirm(data);
      } else {
        const errorData = await response.json();
        
        // Check if error is specifically about payment method
        if (errorData.detail?.requires_payment_setup || 
            errorData.detail?.type === 'payment_validation_required') {
          setNeedsPaymentSetup(true);
          
          if (onPaymentSetupRequired) {
            onPaymentSetupRequired();
          } else {
            alert('Please add a payment method before subscribing. Go to your Profile to add a payment method.');
          }
          return;
        }
        
        // Handle other error types
        let errorMessage = 'Please try again';
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail?.message) {
          errorMessage = errorData.detail.message;
        }
        alert(`Subscription failed: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      alert('Failed to create subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  
  const selectedPricing = selectedPlan ? SUBSCRIPTION_PRICING[selectedPlan] : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-full sm:max-w-2xl md:max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 pr-2">
            {confirmationStep ? 'Confirm Subscription' : 'Choose Your Plan'}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 flex-shrink-0">
            <X size={20} className="sm:w-6 sm:h-6" />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {!confirmationStep ? (
            // Step 1: Plan Selection
            <>
              {/* Plan Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                {/* Monthly Plan */}
                <div 
                  className={`border-2 rounded-xl p-4 sm:p-6 cursor-pointer transition-all ${
                    selectedPlan === 'Monthly' 
                      ? 'border-blue-500 bg-blue-50 shadow-lg' 
                      : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                  }`}
                  onClick={() => setSelectedPlan('Monthly')}
                >
                  <div className="flex justify-between items-start mb-3 sm:mb-4">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900">Monthly Plan</h3>
                    {selectedPlan === 'Monthly' && <Check className="text-blue-500" size={20} />}
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-2">
                    $20<span className="text-base sm:text-lg text-slate-600">/month</span>
                  </div>
                  <div className="text-xs sm:text-sm text-slate-600 mb-3 sm:mb-4">Billed monthly</div>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="flex items-start gap-2">
                      <Check size={14} className="text-green-500 flex-shrink-0 mt-0.5 sm:w-4 sm:h-4" />
                      <span className="break-words">Unlimited Access to Primary Services</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check size={14} className="text-green-500 flex-shrink-0 mt-0.5 sm:w-4 sm:h-4" />
                      <span className="break-words">50% Discount on Premium Services</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check size={14} className="text-green-500 flex-shrink-0 mt-0.5 sm:w-4 sm:h-4" />
                      <span className="break-words">Earn Rewards Points Every Login</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check size={14} className="text-green-500 flex-shrink-0 mt-0.5 sm:w-4 sm:h-4" />
                      <span className="break-words">14 Day Risk-free Trial - Full Refund</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check size={14} className="text-green-500 flex-shrink-0 mt-0.5 sm:w-4 sm:h-4" />
                      <span className="break-words">Cancel Anytime From Profile</span>
                    </div>
                  </div>
                </div>

                {/* Annual Plan */}
                <div 
                  className={`border-2 rounded-xl p-4 sm:p-6 cursor-pointer transition-all relative ${
                    selectedPlan === 'Annual' 
                      ? 'border-blue-500 bg-blue-50 shadow-lg' 
                      : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                  }`}
                  onClick={() => setSelectedPlan('Annual')}
                >
                  <div className="absolute -top-2 sm:-top-3 right-3 sm:right-4 bg-green-500 text-white text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full">
                    SAVE $40
                  </div>
                  <div className="flex justify-between items-start mb-3 sm:mb-4">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900">Annual Plan</h3>
                    {selectedPlan === 'Annual' && <Check className="text-blue-500" size={20} />}
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-1">
                    $200<span className="text-base sm:text-lg text-slate-600">/year</span>
                  </div>
                  <div className="text-xs sm:text-sm text-green-600 font-medium mb-3 sm:mb-4">Just $16.67/month</div>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="flex items-start gap-2">
                      <Check size={14} className="text-green-500 flex-shrink-0 mt-0.5 sm:w-4 sm:h-4" />
                      <span className="break-words">Unlimited Access to Primary Services</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check size={14} className="text-green-500 flex-shrink-0 mt-0.5 sm:w-4 sm:h-4" />
                      <span className="break-words">50% Discount on Premium Services</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check size={14} className="text-green-500 flex-shrink-0 mt-0.5 sm:w-4 sm:h-4" />
                      <span className="break-words">Earn Rewards Points Every Login</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check size={14} className="text-green-500 flex-shrink-0 mt-0.5 sm:w-4 sm:h-4" />
                      <span className="break-words">14 Day Risk-free Trial - Full Refund</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check size={14} className="text-green-500 flex-shrink-0 mt-0.5 sm:w-4 sm:h-4" />
                      <span className="break-words">Cancel Anytime From Profile</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={handleProceedToConfirmation}
                disabled={!selectedPlan}
                className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-blue-600 text-white text-base sm:text-lg font-semibold rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
              >
                Continue to Confirmation
              </button>
            </>
          ) : (
            // Step 2: Confirmation
            <>
              {/* Selected Plan Summary */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-3">
                  <div className="flex-1">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900">{selectedPricing.label} Subscription</h3>
                    <div className="text-2xl sm:text-3xl font-bold text-blue-600 mt-2">
                      ${selectedPricing.price.toFixed(2)}
                      <span className="text-base sm:text-lg text-slate-600">/{selectedPricing.period}</span>
                    </div>
                    {selectedPricing.savings && (
                      <div className="text-xs sm:text-sm text-green-600 font-medium mt-1">{selectedPricing.savings}</div>
                    )}
                  </div>
                  <button 
                    onClick={() => setConfirmationStep(false)}
                    className="text-blue-600 text-xs sm:text-sm hover:underline whitespace-nowrap"
                  >
                    Change Plan
                  </button>
                </div>
              </div>

              {/* Important Information */}
              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <DollarSign size={18} className="text-yellow-600 flex-shrink-0 mt-0.5 sm:w-5 sm:h-5" />
                    <div>
                      <div className="font-semibold text-yellow-900 mb-1 text-sm sm:text-base">Charged Immediately</div>
                      <div className="text-xs sm:text-sm text-yellow-800 break-words">
                        ${selectedPricing.price.toFixed(2)} will be charged to your payment method immediately upon confirmation.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <Shield size={18} className="text-green-600 flex-shrink-0 mt-0.5 sm:w-5 sm:h-5" />
                    <div>
                      <div className="font-semibold text-green-900 mb-1 text-sm sm:text-base">14-Day Refund Window</div>
                      <div className="text-xs sm:text-sm text-green-800 break-words">
                        Cancel within 14 days for a full refund. After 14 days, your subscription continues until the end of the billing period.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 sm:p-4">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <RefreshCw size={18} className="text-slate-600 flex-shrink-0 mt-0.5 sm:w-5 sm:h-5" />
                    <div>
                      <div className="font-semibold text-slate-900 mb-1 text-sm sm:text-base">Billing & Renewal</div>
                      <div className="text-xs sm:text-sm text-slate-700 break-words">
                        Next billing: {selectedPlan === 'Monthly' ? 'Same day next month at 12:01 AM MST' : 'Same day next year at 12:01 AM MST'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Auto-Renew Option */}
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                <label className="flex items-start gap-2 sm:gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRenew}
                    onChange={(e) => setAutoRenew(e.target.checked)}
                    className="mt-0.5 sm:mt-1 h-4 w-4 sm:h-5 sm:w-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500 flex-shrink-0"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-blue-900 mb-1 flex items-center gap-2 text-sm sm:text-base">
                      <RefreshCw size={14} className="sm:w-4 sm:h-4" />
                      Enable Auto-Renewal (Recommended)
                    </div>
                    <div className="text-xs sm:text-sm text-blue-800 break-words">
                      Your subscription will automatically renew at the end of each billing period. Cancel anytime from your profile.
                    </div>
                  </div>
                </label>
              </div>

              {/* Confirmation Notice */}
              <div className="bg-slate-100 border-l-4 border-slate-600 rounded p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex items-start gap-2 sm:gap-3">
                  <AlertCircle size={18} className="text-slate-600 flex-shrink-0 mt-0.5 sm:w-5 sm:h-5" />
                  <div className="text-xs sm:text-sm text-slate-700">
                    <p className="font-semibold mb-1">By clicking "Confirm & Subscribe" below, you agree to:</p>
                    <ul className="list-disc list-inside space-y-1 ml-1 sm:ml-2 break-words">
                      <li>Immediate payment of ${selectedPricing.price.toFixed(2)}</li>
                      <li>Automatic renewal {autoRenew ? '(enabled)' : '(disabled)'}</li>
                      <li>14-day refund policy terms</li>
                      <li>Terms of Service and Privacy Policy</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  onClick={() => setConfirmationStep(false)}
                  className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 border-2 border-slate-300 rounded-lg text-slate-700 font-semibold hover:bg-slate-50 transition-colors text-sm sm:text-base"
                >
                  Back
                </button>
                <button
                  onClick={handleFinalConfirmation}
                  disabled={loading}
                  className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  {loading ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Shield size={18} className="sm:w-5 sm:h-5" />
                      Confirm & Subscribe
                    </>
                  )}
                </button>
              </div>

              {/* Terms */}
              <div className="mt-3 sm:mt-4 text-[10px] sm:text-xs text-slate-500 text-center break-words">
                <p>Secure payment processing. You will receive a confirmation email.</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
